import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { MDXProvider } from "@mdx-js/react";
import InlineCTA from "./components/blog/InlineCTA";
import ErrorBoundary from "./components/ErrorBoundary";
import App from "./App.tsx";
import { initAnalytics, capture } from "./lib/analytics";
import { captureAttribution, hasAnyUtm, attributionPayload } from "./lib/attribution";
import { MetaPixel, GoogleAdsTag } from "./components/attribution/MetaPixel";
import "./index.css";

// Fire PostHog init before the app mounts so page-view capture catches the
// initial navigation. No-op if VITE_POSTHOG_KEY isn't set (dev / pre-launch).
initAnalytics();

// Capture UTMs from the initial landing URL once, then emit anonymous funnel
// events to PostHog only. Anonymous inserts against `analytics_events` would
// be rejected by RLS (auth.uid() = user_id) — useAnalytics short-circuits on
// !user for that reason. Ads Agent consumes these from PostHog.
{
  const attribution = captureAttribution();
  const path =
    typeof window !== "undefined" ? window.location.pathname : "/";
  capture("landing_viewed", {
    path,
    has_utm: hasAnyUtm(attribution),
    ...attributionPayload(attribution),
  });
  if (hasAnyUtm(attribution)) {
    capture("ad_clicked", {
      landing_path: attribution?.landing_path ?? path,
      ...attributionPayload(attribution),
    });
  }
}

const mdxComponents = {
  InlineCTA,
};

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Only enable in production so local dev stays noise-free
  enabled: import.meta.env.PROD && !!import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // Capture 5% of sessions, 100% of sessions with errors
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.05,
  replaysOnErrorSampleRate: 1.0,
});

const metaPixelId = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
const googleAdsId = import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined;

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center p-8 text-center">
        <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
        <p className="text-muted-foreground text-sm mb-6">
          {error instanceof Error ? error.message : "An unexpected error occurred"}
        </p>
        <div className="flex gap-3">
          <button
            onClick={resetError}
            className="px-4 py-2 rounded-lg border border-border text-sm hover:bg-secondary transition-colors"
          >
            Try Again
          </button>
          <a
            href="/"
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm hover:opacity-90 transition-opacity"
          >
            Go Home
          </a>
        </div>
      </div>
    )}
  >
    <ErrorBoundary>
      <HelmetProvider>
        <MDXProvider components={mdxComponents}>
          <MetaPixel pixelId={metaPixelId} />
          <GoogleAdsTag tagId={googleAdsId} />
          <App />
        </MDXProvider>
      </HelmetProvider>
    </ErrorBoundary>
  </Sentry.ErrorBoundary>
);
